import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, Stack, IconButton, Divider, useMediaQuery } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const TipTapEditor = forwardRef(({
  value,
  defaultValue,
  onBlur,
  onChange,
  editable = true,
  placeholder = '',
  toolBarVisible,
  minHeight = 150,
}, ref) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const containerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder,
        emptyNodeClass: 'is-empty',
      }),
    ],
    content: value ?? defaultValue ?? '',
    editable,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onFocus: () => setFocused(true),
    onBlur: ({ editor }) => {
      setFocused(false);
      onBlur?.(editor.getHTML());
    },
  });

  // Sync controlled `value`
  useEffect(() => {
    if (value !== undefined && editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  // Expose imperative methods
  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.setContent(''),
    getHTML: () => editor?.getHTML(),
    insertEmoticon: (emoji) => editor?.chain().focus().insertContent(emoji).run(),
  }), [editor]);

  const renderButton = (icon, onClick, active) => (
    <IconButton
      size="small"
      sx={{ '& svg': { fontSize: '16px' } }}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      color={active ? 'primary' : 'default'}
    >
      {icon}
    </IconButton>
  );

  const handleShiftKeyDown = (e) => e.shiftKey && e.stopPropagation();
  const handleShiftKeyUp = (e) => (e.key === "Shift") && e.stopPropagation();

  if (!editor) return null;

  const showToolbar =
    toolBarVisible === true ||
    (toolBarVisible === undefined && (focused || hovered));

  return (
    <Box
      ref={containerRef}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Collapsing toolbar */}
      <Box
        sx={{
          overflow: 'hidden',
          transition: 'opacity 0.2s ease, max-height 0.25s ease, margin-bottom 0.25s ease',
          opacity: showToolbar ? 1 : 0,
          maxHeight: showToolbar ? 48 : 0,
          mb: showToolbar ? 1 : 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}
        >
          {renderButton(
            <FormatBoldIcon />,
            () => editor.chain().focus().toggleBold().run(),
            editor.isActive('bold')
          )}
          {renderButton(
            <FormatItalicIcon />,
            () => editor.chain().focus().toggleItalic().run(),
            editor.isActive('italic')
          )}
          {renderButton(
            <FormatListBulletedIcon />,
            () => editor.chain().focus().toggleBulletList().run(),
            editor.isActive('bulletList')
          )}
          {renderButton(
            <FormatListNumberedIcon />,
            () => editor.chain().focus().toggleOrderedList().run(),
            editor.isActive('orderedList')
          )}
          {renderButton(
            <FormatQuoteIcon />,
            () => editor.chain().focus().toggleBlockquote().run(),
            editor.isActive('blockquote')
          )}
        </Stack>
        <Divider sx={{ mt: 1 }} />
      </Box>

      {/* Editor content */}
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          minHeight,
          '& .ProseMirror': {
            outline: 'none',
            padding: 1,
            minHeight,
            cursor: editable ? 'text' : 'default',
            position: 'relative',
            '& p:first-of-type': { marginTop: 0 },
            '& p.is-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              color: '#888',
              fontStyle: 'italic',
              position: 'absolute',
              left: 8,
              top: 8,
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
            },
          },
        }}
      >
        <EditorContent
          editor={editor}
          onKeyDown={handleShiftKeyDown}
          onKeyUp={handleShiftKeyUp}
        />
      </Box>
    </Box>
  );
});

export default TipTapEditor;
